// Client for the data Worker. Reads VITE_WORKER_URL + VITE_DASHBOARD_API_KEY from env.

const WORKER_URL = import.meta.env.VITE_WORKER_URL || '';
const API_KEY = import.meta.env.VITE_DASHBOARD_API_KEY || '';

async function get<T>(path: string): Promise<T> {
  const res = await fetch(`${WORKER_URL}${path}`, {
    headers: API_KEY ? { Authorization: `Bearer ${API_KEY}` } : {},
  });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json();
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${WORKER_URL}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(API_KEY ? { Authorization: `Bearer ${API_KEY}` } : {}),
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
  return res.json();
}

export const api = {
  sources: () => get<{ sources: Array<{ name: string; enabled: boolean; reason?: string }> }>('/sources'),
  snapshot: (property: string, weeks = 12) =>
    get<{ snapshots: Array<Record<string, unknown>> }>(
      `/api/snapshot?property=${property}&weeks=${weeks}`
    ),
  articles: (property: string) =>
    get<{ articles: Array<Record<string, unknown>> }>(`/api/articles?property=${property}`),
  articlesPublishedPerformance: (property: string) =>
    get<{
      articles: Array<Record<string, unknown>>;
      weekEnding: string | null;
    }>(`/api/articles-published-performance?property=${property}`),
  instagram: (property: string) =>
    get<{ instagram: Array<Record<string, unknown>> }>(`/api/instagram?property=${property}`),
  newslettersLists: () =>
    get<{ lists: Array<Record<string, unknown>> }>('/api/newsletters/lists'),
  newslettersListTrend: (listId: string, weeks = 12) =>
    get<{ list_id: string; snapshots: Array<Record<string, unknown>> }>(
      `/api/newsletters/list/${encodeURIComponent(listId)}?weeks=${weeks}`
    ),
  moneyPages: (property: string, limit = 40) =>
    get<{ money_pages: Array<Record<string, unknown>> }>(
      `/api/money-pages?property=${property}&limit=${limit}`
    ),
  runLog: () => get<{ runs: Array<Record<string, unknown>> }>('/api/run-log'),
  bookingsByAccountManager: (tenants?: string[]) =>
    get<{
      weekEnding: string | null;
      rows: Array<{ account_manager: string; count: number; total_sgd: number }>;
      unattributed: { count: number; total_sgd: number };
      tenants: string[];
    }>(
      `/api/bookings-by-account-manager${
        tenants && tenants.length > 0 ? `?tenants=${tenants.join(',')}` : ''
      }`
    ),
  bookingsSummary: (tenants?: string[]) =>
    get<{
      mtd: {
        rangeStart: string;
        rangeEnd: string;
        rows: Array<{ account_manager: string; count: number; total_sgd: number }>;
        unattributed: { count: number; total_sgd: number };
      };
      trailing12: {
        rangeStart: string;
        rangeEnd: string;
        rows: Array<{ account_manager: string; count: number; total_sgd: number }>;
        unattributed: { count: number; total_sgd: number };
      };
      tenants: string[];
    }>(
      `/api/bookings-summary${
        tenants && tenants.length > 0 ? `?tenants=${tenants.join(',')}` : ''
      }`
    ),
  triggerRun: () => post<{ ok: boolean; results: unknown[] }>('/run-now', {}),
  manual: {
    productionJob: (data: Record<string, unknown>) => post('/manual/production-job', data),
    event: (data: Record<string, unknown>) => post('/manual/event', data),
    salesPipeline: (data: Record<string, unknown>) => post('/manual/sales-pipeline', data),
  },
};

export function isConfigured(): boolean {
  return Boolean(WORKER_URL);
}
